import ReactDOM from 'react-dom/client';
// Load third-party component library first so our local `index.css` can override it where needed.
import '@cloudclevr/components/style.css';
import './index.css';
import './cloudclevr-overrides.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import installImpersonationListener from './impersonationListener';
import { BrowserRouter as Router } from 'react-router-dom';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <Router>
    <App />
  </Router>
);

// Global message listener for impersonation iframe logout
// Install impersonation listener at runtime. In tests the listener can be installed by importing the module.
(() => {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.JEST_WORKER_ID) return;
    installImpersonationListener(window, document);
  } catch (e) {
    // ignore
  }
})();

// Portal wrapper setup
(() => {
  // Ensure portal wrapper doesn't block interactions on initial load
  try {
    const ensureWrapper = (id = 'modal-container') => {
      let wrapper = document.getElementById(id) as HTMLDivElement | null;
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.id = id;
        document.body.appendChild(wrapper);
      }

      // Let CSS control pointer-events by default (use data attribute to enable interactivity).
      // Avoid setting inline `pointerEvents` here to reduce race conditions with overlays.

      const isVisibleChild = (el: Element) => {
        try {
          const rect = (el as HTMLElement).getBoundingClientRect?.();
          if (rect && (rect.width > 0 || rect.height > 0)) return true;
          const cs = window.getComputedStyle(el as Element);
          return cs && cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
        } catch (err) {
          return false;
        }
      };

      const cleanupChildren = () => {
        try {
          Array.from(wrapper!.children).forEach((child) => {
            const el = child as HTMLElement;
            const isEmpty = el.children.length === 0 && !el.textContent?.trim();
            const rect = el.getBoundingClientRect?.();
            const isZeroSize = rect && rect.width === 0 && rect.height === 0;
            if (isEmpty || isZeroSize) {
              try {
                // Avoid removing nodes that React may still be unmounting.yes to both
                // Hide and mark with timestamp, then attempt a safe delayed removal
                el.style.display = 'none';
                el.setAttribute('data-cc-hidden', 'true');
                el.setAttribute('data-cc-hidden-at', Date.now().toString());

                // Schedule safe cleanup after short delay
                // Skip scheduling long-running cleanup during Jest tests to avoid open handles
                if (typeof process !== 'undefined' && process.env && process.env.JEST_WORKER_ID) {
                  return;
                }
                setTimeout(() => {
                  try {
                    if (el.getAttribute('data-cc-hidden') !== 'true') return;
                    // Check for React internal properties on the node to avoid removing
                    const ownProps = Object.getOwnPropertyNames(el as any);
                    const hasReactInternals = ownProps.some((n) => n.startsWith('__react'));
                    if (hasReactInternals) return;
                    if (el.parentNode && Array.from(el.parentNode.childNodes).includes(el)) {
                      el.parentNode.removeChild(el);
                    }
                  } catch (err) {
                    // ignore
                  }
                }, 3000);
              } catch (e) {
                /* ignore */
              }
            }
          });
        } catch (err) {
          // ignore
        }
      };

      const applyState = () => {
        try {
          cleanupChildren();
          // If an overlay explicitly set the data attribute, leave interactivity to CSS.
          if (wrapper!.getAttribute('data-cc-overlay-active') === 'true') return;
          const anyVisible = Array.from(wrapper!.children).some(isVisibleChild);
          wrapper!.style.pointerEvents = anyVisible ? 'auto' : 'none';
        } catch (err) {
          // ignore
        }
      };

      // Observe direct child changes and re-evaluate pointer-events
      const observer = new MutationObserver(() => applyState());
      observer.observe(wrapper, { childList: true, subtree: true });

      // As a safety, re-evaluate on navigation/resume (popstate) and focus
      window.addEventListener('popstate', applyState);
      window.addEventListener('focus', applyState);

      // Initial pass
      applyState();

      // Keep reference on the element so dev tools can inspect it if needed
      (wrapper as any).__cc_modal_observer__ = observer;
    };

    ensureWrapper('modal-container');
  } catch (e) {
    // ignore: defensive DOM manipulation, non-critical
  }
})();

reportWebVitals();
